/**
 * « Mettez ces produits de côté » — la fenêtre qui s'ouvre sur les trois caisses.
 *
 * Elle ne se ferme que par un clic qui vaut « c'est fait », et ce clic vaut pour
 * TOUT LE MONDE : une seule personne va chercher le fromage, les deux autres postes
 * cessent de le réclamer dans les quinze secondes et voient qui y est allé.
 *
 * Choix d'affichage tirés des mesures de production :
 *  - tri par stock RESTANT croissant. Les œufs (Crémerie, 243 en rayon) entrent par
 *    la règle de rayon — la refuser produit par produit serait recommencer le débat
 *    que `preparation-froid.ts` a tranché — mais ils tombent en fin de liste, derrière
 *    tout ce qui est réellement en danger. Sur la commande de l'incident (44 lignes),
 *    la fenêtre en affiche 8, le chèvre à 0 en tête.
 *  - la quantité affichée est celle COMMANDÉE. La quantité courante d'une ligne est
 *    écrasée par la pesée de la préparation (mesuré : 120 g commandés, 184 g affichés).
 *  - overlay en z-[90] : au-dessus des dialogues du POS (z-50 à z-[80]) et sous le
 *    clavier virtuel (z-[10000]). `ui/Modal` est figé à z-50, il passerait dessous.
 */
import { useEffect, useMemo } from 'react';
import { Snowflake, AlertTriangle, Check, WifiOff } from 'lucide-react';
import { usePOSStore } from '../../hooks/usePOSStore';
import { formatQty } from '../../utils/pos.utils';
import type { EtatMisesDeCote } from '../../hooks/useMisesDeCote';
import type { ArticleAMettreDeCote, CommandeAMettreDeCote } from '../../api/misesDeCote.api';

interface Props {
  etat: EtatMisesDeCote;
}

/** « × 2 », « 1.5 kg », « 300 g » — ce qu'on doit aller prendre, pas un nombre nu. */
function libelleQuantite(article: ArticleAMettreDeCote): string {
  const q = Number(article.quantite ?? 0);
  const unite = (article.unite || '').toLowerCase();
  if (unite === 'kg') return `${formatQty(q)} kg`;
  if (unite === 'grams' || unite === 'g') {
    return q >= 1000 ? `${formatQty(q / 1000)} kg` : `${formatQty(q)} g`;
  }
  return `× ${formatQty(q)}`;
}

/** Ce qu'il reste EN RAYON, relu à chaque tour de ronde — il bouge entre deux lectures. */
/**
 * ⚠️ Le chiffre affiché est ce qui reste APRÈS que cette commande a été déduite —
 * la commande retire le stock dès sa création. Il vaut donc 0 précisément quand
 * la marchandise à aller chercher est, elle, encore sur le rayon. Écrire « 0 en
 * rayon » ferait conclure « il n'y a rien à prendre » et laisserait le fromage
 * partir au comptoir : le mot doit dire ce que le chiffre mesure.
 */
function libelleStock(article: ArticleAMettreDeCote): string {
  if (article.stock_rayon === null || article.stock_rayon === undefined) return 'stock inconnu';
  const unite = (article.stock_unite || '').toLowerCase();
  const reste = Number(article.stock_rayon);
  const reserve = Number(article.stock_reserve ?? 0);
  const enReserve = reserve > 0 ? ` · ${formatQty(reserve)}${unite === 'kg' ? ' kg' : ''} en réserve` : '';
  // Rayon vide n'est pas « le dernier » quand la réserve en tient encore
  // (boisson d'avoine, 08/10/2026 : « c'est le dernier » avec huit en réserve).
  if (reste <= 0) return reserve > 0 ? `rien en rayon${enReserve}` : 'rien derrière : c\'est le dernier';
  const valeur = formatQty(article.stock_rayon);
  if (unite === 'kg') return `${valeur} kg derrière celui-ci${enReserve}`;
  return `${valeur} derrière celui-ci${enReserve}`;
}

function libelleRetrait(commande: CommandeAMettreDeCote): string {
  if (!commande.delivery_date) return 'retrait non daté';
  const jour = new Date(commande.delivery_date).toLocaleDateString('fr-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  // Le magasin ne promet pas d'heure (le client vient au message « commande prête ») :
  // on n'en affiche une que si le réglage en prévoit vraiment une.
  return commande.pickup_slot ? `Retrait ${jour}, ${commande.pickup_slot}` : `Retrait ${jour}`;
}

function heureCourte(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
}

export default function FenetreMiseDeCote({ etat }: Props) {
  const {
    commandes,
    nombreAFaire,
    visible,
    enLigne,
    acquittementEnCours,
    erreur,
    dernierAcquittement,
    acquitter,
    plusTard,
    rappelerDemain,
    oublierDernierAcquittement,
  } = etat;

  /**
   * Les identifiants RÉELLEMENT affichés à l'instant du clic. C'est eux qu'on
   * acquitte, et rien d'autre : le sondage rafraîchit la liste toutes les 15 s
   * pendant que la fenêtre est ouverte, et une commande qui entre pendant qu'on
   * est au frigo ne doit pas être acquittée par le clic de retour. Mesuré en
   * production : 3 commandes sur 11 arrivent en cours de journée.
   */
  const idsAffiches = useMemo(
    () => commandes.flatMap((c) => c.articles.map((a) => a.line_id)),
    [commandes],
  );

  // La douchette écoute le `document` : fenêtre ouverte, un code-barres scanné
  // partirait dans le panier CACHÉ derrière elle (incident ricotta du 04/09/2026).
  // On la suspend le temps de l'affichage. Elle ne s'ouvre que sur l'onglet Caisse,
  // écran libre — voir le décideur dans `useMisesDeCote`.
  const setScanSuppressed = usePOSStore((s) => s.setScanSuppressed);
  useEffect(() => {
    if (!visible) return;
    setScanSuppressed(true);
    return () => setScanSuppressed(false);
  }, [visible, setScanSuppressed]);

  // Le bandeau « untel y est allé » s'efface tout seul : c'est une information,
  // pas une action, et il ne doit rien recouvrir durablement.
  useEffect(() => {
    if (!dernierAcquittement) return;
    const t = setTimeout(oublierDernierAcquittement, 20000);
    return () => clearTimeout(t);
  }, [dernierAcquittement, oublierDernierAcquittement]);

  if (!visible) {
    if (!dernierAcquittement) return null;
    return (
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[90] max-w-[90vw]">
        <button
          onClick={oublierDernierAcquittement}
          className="flex items-center gap-3 px-5 py-3 min-h-[56px] bg-emerald-600 text-white rounded-xl shadow-2xl font-semibold"
        >
          <Check className="w-6 h-6 shrink-0" />
          <span>
            Produits mis de côté
            {/* Sans nom, c'est que le serveur n'a pas renvoyé l'auteur : on dit ce
                qu'on sait (un autre poste) plutôt que d'inventer un prénom. */}
            {dernierAcquittement.par_nom
              ? ` par ${dernierAcquittement.par_nom}`
              : ' sur un autre poste'}
            {dernierAcquittement.at ? ` à ${heureCourte(dernierAcquittement.at)}` : ''}
          </span>
        </button>
      </div>
    );
  }

  // Une commande à retirer aujourd'hui n'est jamais reportée : on le dit.
  const aujourdhuiIso = new Date().toLocaleDateString('fr-CA', { timeZone: 'Europe/Brussels' });
  const retraitAujourdhui = commandes.some(
    (c) => c.delivery_date && String(c.delivery_date).slice(0, 10) <= aujourdhuiIso,
  );

  const commandesAffichees = commandes
    .map((c) => ({
      ...c,
      articles: [...(c.articles || [])].sort(
        (a, b) => Number(a.stock_rayon ?? 9e9) - Number(b.stock_rayon ?? 9e9),
      ),
    }))
    .filter((c) => c.articles.length > 0);

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-sky-600 to-cyan-600 px-6 py-5 shrink-0">
          <div className="flex items-center gap-3">
            <Snowflake className="w-9 h-9 text-white shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-white">Produits à mettre de côté</h2>
              <p className="text-sky-100 text-sm mt-1">
                Ils sont encore en rayon et peuvent être vendus au comptoir. Allez les chercher
                maintenant, puis confirmez ci-dessous.
              </p>
            </div>
          </div>
        </div>

        {/* Commandes */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {commandesAffichees.length === 0 && (
            <p className="text-center text-gray-500 py-8">
              Rien à mettre de côté pour le moment.
            </p>
          )}

          {commandesAffichees.map((commande) => (
            <div key={commande.order_id} className="border-2 border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="font-bold text-lg text-gray-900">
                  {commande.customer_name || 'Client sans nom'}
                </div>
                <div className="text-sm text-gray-600">
                  {libelleRetrait(commande)}
                  {commande.order_number ? ` · ${commande.order_number}` : ''}
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {commande.articles.map((article) => (
                  <div key={article.line_id} className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900">{article.product_name}</div>
                      <div className="text-sm text-gray-500">
                        {article.rayon || 'rayon inconnu'}
                        {article.froid ? ' · frigo' : ''}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-xl font-bold text-gray-900">{libelleQuantite(article)}</div>
                      <div
                        className={`text-sm font-medium ${
                          Number(article.stock_rayon ?? 0) <= 0 && Number(article.stock_reserve ?? 0) <= 0
                            ? 'text-red-600'
                            : 'text-amber-700'
                        }`}
                      >
                        {libelleStock(article)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Pied : le geste unique */}
        <div className="shrink-0 border-t border-gray-200 px-5 py-4 space-y-3 bg-white">
          {erreur && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>{erreur}</span>
            </div>
          )}

          {!enLigne ? (
            <>
              {/* Hors ligne, l'acquittement ne peut pas atteindre les autres postes.
                  On le dit, et on ne bloque pas la caisse pour autant. */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <WifiOff className="w-5 h-5 shrink-0 mt-0.5" />
                <span>
                  Sans réseau, impossible de prévenir les autres postes. Mettez les produits de côté,
                  la confirmation sera possible dès le retour du réseau.
                </span>
              </div>
              <button
                onClick={plusTard}
                className="w-full min-h-[56px] px-6 py-4 bg-gray-200 text-gray-800 rounded-xl font-bold text-lg active:bg-gray-300"
              >
                Fermer — la fenêtre reviendra plus tard
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => void acquitter(idsAffiches)}
                disabled={acquittementEnCours}
                className="w-full min-h-[56px] px-6 py-4 bg-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg active:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-3"
              >
                <Check className="w-7 h-7" />
                {/* Rien à faire = fenêtre rouverte à la main après coup : elle doit
                    se refermer, sinon un bouton grisé enferme la caisse. */}
                {nombreAFaire === 0
                  ? 'Fermer'
                  : acquittementEnCours
                    ? 'Enregistrement…'
                    : "C'est mis de côté"}
              </button>

              {/* LA PORTE DE SORTIE. Sans elle, un vendeur avec un client au comptoir
                  n'a qu'un seul geste pour récupérer son écran : déclarer « c'est
                  mis de côté » pour des produits que personne n'est allé chercher —
                  et ce mensonge éteint la réclamation sur les trois caisses. Mieux
                  vaut un rappel dans un quart d'heure qu'un acquittement de
                  complaisance. Volontairement discret : c'est le second choix. */}
              {nombreAFaire > 0 && (
                <button
                  onClick={plusTard}
                  disabled={acquittementEnCours}
                  className="w-full min-h-[56px] px-6 py-4 bg-white text-gray-600 border-2 border-gray-300 rounded-xl font-semibold text-base active:bg-gray-100 disabled:opacity-50"
                >
                  Pas maintenant — me le rappeler dans 15 min
                </button>
              )}

              {/* Retrait dans plusieurs jours : rien ne presse aujourd'hui. Vaut
                  pour les trois caisses ; le serveur garde les retraits du jour. */}
              {nombreAFaire > 0 && (
                <button
                  onClick={() => void rappelerDemain(idsAffiches)}
                  disabled={acquittementEnCours}
                  className="w-full min-h-[56px] px-6 py-4 bg-white text-gray-600 border-2 border-gray-300 rounded-xl font-semibold text-base active:bg-gray-100 disabled:opacity-50"
                >
                  Me le rappeler demain
                  {retraitAujourdhui ? ' (sauf les retraits d’aujourd’hui)' : ''}
                </button>
              )}
            </>
          )}

          {nombreAFaire > 0 && (
            <p className="text-xs text-gray-500 text-center">
              Un seul clic suffit pour les trois caisses : les autres postes cesseront de le réclamer.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
